import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../core/context/AuthContext";

const AiCreditsUsageContext = createContext(null);

/**
 * Shared “out of AI credits” notice for the business shell.
 *
 * Call ``notifyUsageLimitReached()`` from any metered-action catch site; the
 * single banner above the action-items list reads this flag.
 *
 * @example
 * const { notifyUsageLimitReached } = useAiCreditsUsage();
 * if (isUsageLimitError(err)) { notifyUsageLimitReached(); return; }
 */
export const useAiCreditsUsage = () => {
  const ctx = useContext(AiCreditsUsageContext);
  if (!ctx) {
    throw new Error("useAiCreditsUsage must be used within AiCreditsUsageProvider");
  }
  return ctx;
};

/** Safe outside the provider (e.g. settings demo) — no-ops when absent. */
export const useAiCreditsUsageOptional = () => useContext(AiCreditsUsageContext);

/**
 * @example
 * <AiCreditsUsageProvider><Outlet /></AiCreditsUsageProvider>
 *
 * @example
 * // Chrome extension — no AuthProvider; remaining-credits auto-clear is a no-op
 * <AiCreditsUsageProvider><PopupApp /></AiCreditsUsageProvider>
 */
export const AiCreditsUsageProvider = ({ children }) => {
  const auth = useAuth();
  const user = auth?.user;
  const [usageLimitReached, setUsageLimitReached] = useState(false);

  const notifyUsageLimitReached = useCallback(() => {
    setUsageLimitReached(true);
  }, []);

  const dismissUsageLimitReached = useCallback(() => {
    setUsageLimitReached(false);
  }, []);

  useEffect(() => {
    const remaining = Number(user?.ai_credits_remaining);
    if (Number.isFinite(remaining) && remaining > 0) {
      setUsageLimitReached(false);
    }
  }, [user?.ai_credits_remaining]);

  // Bridge for non-hook helpers (usageOrErrorMessage) without prop-drilling.
  useEffect(() => {
    const onLimit = () => setUsageLimitReached(true);
    window.addEventListener("starmesh:ai-credits-usage-limit", onLimit);
    return () => {
      window.removeEventListener("starmesh:ai-credits-usage-limit", onLimit);
    };
  }, []);

  const value = useMemo(
    () => ({
      usageLimitReached,
      notifyUsageLimitReached,
      dismissUsageLimitReached,
    }),
    [usageLimitReached, notifyUsageLimitReached, dismissUsageLimitReached]
  );

  return (
    <AiCreditsUsageContext.Provider value={value}>
      {children}
    </AiCreditsUsageContext.Provider>
  );
};
